import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalAudioService } from '@/services/audio/GlobalAudioService';
import { audioPositionTracker } from '@/services/audio/AudioPositionTracker';

/**
 * Enhanced Navigation Audio Control Hook
 * Smart pause/resume based on navigation context:
 * - Pauses when navigating AWAY from Learn pages (to Dashboard, Profile, etc.)
 * - Pauses when navigating BETWEEN modules (each module = new song)
 * - Pauses on browser tab switch
 * - Saves exact playback position for resume
 * - NO auto-play on navigation or resume
 */
export const useNavigationAudioControl = () => {
  const location = useLocation();
  const previousPath = useRef(location.pathname);

  useEffect(() => {
    // Check if we've navigated to a different page
    if (previousPath.current !== location.pathname) {
      const audioState = GlobalAudioService.getState();
      
      // PHASE 1: Detect module page vs non-module page navigation
      const isModulePage = (path: string) => {
        // Matches: /category-courses/courseId/moduleId
        return /\/[^/]+-courses\/[^/]+\/[^/]+$/.test(path);
      };
      
      const previousWasModule = isModulePage(previousPath.current);
      const currentIsModule = isModulePage(location.pathname);
      
      // If navigating AWAY from a module to non-module page, stop completely
      if (previousWasModule && !currentIsModule) {
        console.log('🎵 [NavigationAudioControl] Navigating away from module page, stopping audio:', {
          from: previousPath.current,
          to: location.pathname
        });
        GlobalAudioService.stop();
      }
      // If staying within module pages, check for course changes
      else if (currentIsModule) {
        // Extract course IDs from paths to detect course changes
        const extractCourseId = (path: string) => {
          const match = path.match(/\/([^/]+)-courses\/([^/]+)/);
          return match ? match[2] : null;
        };
        
        const previousCourseId = extractCourseId(previousPath.current);
        const currentCourseId = extractCourseId(location.pathname);
        const isCourseChange = previousCourseId && currentCourseId && previousCourseId !== currentCourseId;
        
        // If navigating to different course, stop audio completely
        if (isCourseChange) {
          console.log('🎵 [NavigationAudioControl] Course change detected, stopping audio:', {
            from: previousCourseId,
            to: currentCourseId
          });
          
          GlobalAudioService.stop();
        } 
        // Otherwise, just pause if audio is playing
        else if (audioState.isPlaying && !audioState.isPaused) {
          console.log('🎵 [NavigationAudioControl] Navigation detected, pausing audio:', {
            from: previousPath.current,
            to: location.pathname,
            moduleId: audioState.moduleId
          });

          // Save current position before pausing
          if (audioState.moduleId && audioState.courseId && audioState.currentTime > 0) {
            audioPositionTracker.savePosition(
              audioState.moduleId,
              audioState.courseId,
              audioState.currentTime,
              audioState.duration
            );
          }

          GlobalAudioService.pause();
        }
      }

      previousPath.current = location.pathname;
    }
  }, [location.pathname]);

  // Handle browser tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      const audioState = GlobalAudioService.getState();
      
      if (document.hidden && audioState.isPlaying && !audioState.isPaused) {
        console.log('🎵 [NavigationAudioControl] Browser tab hidden, pausing audio');
        
        // Save current position before pausing
        if (audioState.moduleId && audioState.courseId && audioState.currentTime > 0) {
          audioPositionTracker.savePosition(
            audioState.moduleId,
            audioState.courseId,
            audioState.currentTime,
            audioState.duration
          );
        }

        GlobalAudioService.pause();
      }
      
      // Note: We do NOT auto-resume when tab becomes visible
      // User must manually click play button
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
};
